import dynamic from 'next/dynamic';
import Script from 'next/script';
import { useEffect, useState } from 'react';

import type { ISearchOverlay } from 'components/HeaderNavigation/components/MobileNavigation';
import getSearchClient from 'server/repository/algolia/client';
import type { DictionaryItem } from 'service/dictionary/dictionaryService';
import { TrackingService } from 'service/tracking/trackingService';
import { COLORS } from 'styles/constants';
import TemplateMapper from 'templates/TemplateMapper';
import type { IGlobalMetaData } from 'types/GlobalMetadata';

const Grid = dynamic(() => import('components/Grid').then((m: any) => m.Grid), {
  ssr: true,
}) as any;

const GridRow = dynamic(() => import('components/Grid').then((m: any) => m.GridRow), {
  ssr: true,
}) as any;

const UpArrow = dynamic(() => import('components/UpArrow').then((m: any) => m.UpArrow), {
  ssr: true,
}) as any;

const LiquidButton = dynamic(
  () => import('components/LiquidButton').then((m: any) => m.LiquidButton),
  { ssr: true },
) as any;

const Question = dynamic(() => import('components/Question').then((m: any) => m.Question), {
  ssr: true,
}) as any;

const Steps = dynamic(() => import('components/Steps').then((m: any) => m.Steps), {
  ssr: true,
}) as any;

const nonStartPageComponents = ['Question', 'Form', 'EvalancheFormComponent'];

const ClientPromotionsPage = ({
  data,
  globalMetadata,
  searchOverlayData,
  difficultyCopy,
  recipeCopy,
  searchItemLabels,
  promotionsLabels,
}: {
  data: any;
  globalMetadata: IGlobalMetaData;
  searchOverlayData: ISearchOverlay;
  recipeCopy: DictionaryItem[];
  difficultyCopy: Record<string, string>;
  searchItemLabels: Record<string, string>;
  promotionsLabels: Record<string, string>;
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [questions, setQuestions] = useState<any[]>([]);
  const [isStepsExist, setStepsExist] = useState(false);
  const [isStartPage, setStartPage] = useState(false);
  const [hideSteps, setHideSteps] = useState(false);
  const page = data.genericPages?.[0] || {};
  const saveAnswers = page?.savePromotionAnswers;
  const componentsPerStep: any[] = [[]];
  page?.pageComponents.forEach((component: any) => {
    if (component.__typename === 'Separator') {
      componentsPerStep.push([]);
    } else {
      componentsPerStep[componentsPerStep.length - 1].push(component);
    }
  });
  const handleNextStep = () => {
    if (currentStep < componentsPerStep.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  const [searchOverlayDataExtended, setSearchOverlayDataExtended] = useState(searchOverlayData);

  useEffect(() => {
    if (!searchOverlayDataExtended.searchClient) {
      const algoliaClient = getSearchClient();
      setSearchOverlayDataExtended({
        ...searchOverlayDataExtended,
        searchClient: algoliaClient,
      });
    }
  }, [searchOverlayData]);

  useEffect(() => {
    const cg1 = page?.contentGroupingTrackingValue;
    const cg2 = page?.contentGroupingTrackingValues;
    if (cg1) {
      if (cg2) {
        TrackingService.contentGroups({
          cg1: cg1.replace(/_/g, ' '),
          cg2: cg2.replace(/_/g, ' '),
        });
      } else {
        TrackingService.contentGroups({
          cg1: cg1.replace(/_/g, ' '),
        });
      }
    }
    page?.pageComponents.forEach((component: any) => {
      if (component.__typename === 'Separator') {
        setStartPage(true);
        setStepsExist(true);
      }
    });
  }, []);

  useEffect(() => {
    setStartPage(true);
    componentsPerStep[currentStep]?.forEach((component: any) => {
      if (nonStartPageComponents.includes(component.__typename)) {
        setStartPage(false);
      }
    });
    window.scrollTo(0, 0);
  }, [currentStep]);

  const isQuestionPage = componentsPerStep[currentStep]?.find(
    (component: any) => component.__typename === 'Question',
  );

  const handleAnswersInForm = (answers: any) => {
    componentsPerStep.forEach((step: any) => {
      step.forEach((component: any) => {
        if (component.__typename === 'Form') {
          // eslint-disable-next-line no-param-reassign
          component.formFields = component.formFields.filter(
            (field: any) => field.id !== 'promotionAnswers',
          );

          component.formFields.push({
            __typename: 'FormHiddenInput',
            id: 'promotionAnswers',
            fieldName: 'promotionAnswers',
            initialValue: JSON.stringify(answers),
          });
        }
      });
    });
  };

  const handleQuestionSubmit = (answer: string | string[], component: any) => {
    const savedQuestion = questions.find((item) => item.id === component.id);
    const newQuestions = savedQuestion
      ? questions.map((item) => (item.id === component.id ? { ...item, answer } : item))
      : [...questions, { id: component.id, question: component.question, answer }];
    setQuestions(newQuestions);
    handleAnswersInForm(newQuestions);
    handleNextStep();
  };

  return (
    <>
      {page?.cookieBannerRequired === false && (
        <Script type="application/javascript" id="supress-cookie-banner">
          var UC_UI_SUPPRESS_CMP_DISPLAY=true;
        </Script>
      )}
      {isStepsExist ? (
        <>
          {isStartPage || hideSteps ? null : (
            <Steps
              currentStep={currentStep + 1}
              totalSteps={componentsPerStep.length}
              nextStepLabel={promotionsLabels?.next_step || 'Next Step'}
              previousStepLabel={promotionsLabels?.prev_step || 'Prev Step'}
              handleNextStep={handleNextStep}
              handlePreviousStep={handlePreviousStep}
            />
          )}
        </>
      ) : null}
      {isQuestionPage ? (
        <>
          {componentsPerStep[currentStep].map((component: any) => {
            const savedQuestion = questions.find((item) => item.id === component.id);
            const initialValues = savedQuestion ? savedQuestion?.answer : null;
            return component.__typename === 'Question' ? (
              <Question
                key={component.id}
                title={component.title}
                question={component.question}
                subtitle={component.subtitle}
                image={component.image?.asset?.[0]}
                options={component.options}
                saveAnswers={saveAnswers}
                labels={promotionsLabels}
                initialValues={initialValues}
                handleQuestionSubmit={(answer: string | string[]) =>
                  handleQuestionSubmit(answer, component)
                }
              />
            ) : (
              <TemplateMapper
                key={component.id}
                components={[component]}
                copy={page?.dictionary?.dictionaryItems || null}
                recipeCopy={recipeCopy}
                difficultyCopy={difficultyCopy}
                globalMetadata={globalMetadata}
                searchItemLabels={searchItemLabels}
                pageType="promotion"
                formSubmitCallback={() => setHideSteps(true)}
                searchData={searchOverlayDataExtended}
              />
            );
          })}
        </>
      ) : (
        <TemplateMapper
          components={componentsPerStep[currentStep]}
          copy={page?.dictionary?.dictionaryItems || null}
          recipeCopy={recipeCopy}
          difficultyCopy={difficultyCopy}
          globalMetadata={globalMetadata}
          searchItemLabels={searchItemLabels}
          pageType="promotion"
          formSubmitCallback={() => setHideSteps(true)}
          searchData={searchOverlayDataExtended}
        />
      )}
      {isStepsExist && isStartPage ? (
        <Grid>
          <GridRow>
            <button type="button" onClick={handleNextStep}>
              <LiquidButton
                text={promotionsLabels?.next || 'Next'}
                height={50}
                width={250}
                color={COLORS.primary}
                textColor={COLORS.white}
              />
            </button>
          </GridRow>
        </Grid>
      ) : null}
      <UpArrow />
    </>
  );
};

export default ClientPromotionsPage;
